import { supabase } from '@/lib/supabase';
import type { WhatsAppSystemStatusLevel } from '@/services/whatsappSystemStatusService';

export interface PortalWorkerStatus {
  worker_key: string;
  display_name: string;
  role: 'primary' | 'standby' | 'development';
  priority: number;
  enabled: boolean;
  activation_state?: 'active' | 'standby_ready' | 'promoting' | 'fenced' | 'failed';
  fencing_generation?: number;
  status: 'starting' | 'healthy' | 'degraded' | 'offline' | 'auth_required';
  auth_state: 'authenticated' | 'login_required' | 'mfa_required' | 'unknown';
  browser_state: 'running' | 'starting' | 'stopped' | 'error';
  executor_version?: string | null;
  image_version?: string | null;
  viewer_available: boolean;
  last_heartbeat_at?: string | null;
  last_browser_started_at?: string | null;
  last_authenticated_at?: string | null;
  last_job_started_at?: string | null;
  last_job_finished_at?: string | null;
  last_job_status?: string | null;
  last_backup_at?: string | null;
  backup_age_seconds?: number | null;
  last_restore_test_at?: string | null;
  last_restore_test_status?: 'passed' | 'failed' | 'unknown';
  restore_test_age_seconds?: number | null;
  watchdog_checked_at?: string | null;
  watchdog_state?: 'healthy' | 'maintenance' | 'recovering' | 'degraded' | 'escalated' | 'unknown';
  watchdog_layer?: string | null;
  watchdog_restart_count?: number;
  heartbeat_age_seconds?: number | null;
  profile_snapshot_at?: string | null;
  promoted_at?: string | null;
  fenced_at?: string | null;
  has_active_request?: boolean;
}

export interface PortalQuoteHealth {
  status?: string | null;
  created_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  latency_ms?: number | null;
  adapter_version?: string | null;
  offer_count?: number;
  criteria_verified?: boolean;
  failure_code?: string | null;
  source_captured_at?: string | null;
  succeeded?: boolean;
}

export interface PortalAutomationStatus {
  generated_at: string;
  status: WhatsAppSystemStatusLevel;
  headline: string;
  /**
   * Names the broken part when that is all that is wrong, e.g. "bookings OK · quote test failing"
   * (migration 20261010320000). null otherwise, and absent before that migration is live.
   */
  summary_label?: string | null;
  quote_test?: {
    state: 'passing' | 'failing' | 'not_run';
    failing_since?: string | null;
    last_passed_at?: string | null;
    failures_since_last_pass?: number;
  };
  booking_sync?: {
    state: 'ok' | 'attention';
    latest_attempt_status?: string | null;
    last_complete_capture_at?: string | null;
    capture_age_seconds?: number | null;
  };
  executor: {
    preferred: 'self_hosted' | 'steel';
    active?: 'self_hosted' | 'steel' | null;
    viable?: boolean;
    fallback_enabled: boolean;
    fallback_activated?: boolean;
    self_hosted_ready?: boolean;
    steel_ready?: boolean;
    worker_stale_seconds: number;
    updated_at?: string | null;
  };
  fallback?: {
    executor?: 'self_hosted' | 'steel';
    enabled?: boolean;
    ready?: boolean;
    status?: string | null;
    last_validated_at?: string | null;
  };
  profile: {
    id?: string | null;
    display_name?: string | null;
    status?: string | null;
    last_validated_at?: string | null;
    last_session_at?: string | null;
  };
  active_worker_key?: string | null;
  active_worker_role?: PortalWorkerStatus['role'] | null;
  active_worker_age_seconds?: number | null;
  primary_worker_age_seconds?: number | null;
  standby_worker_age_seconds?: number | null;
  primary_ready?: boolean;
  standby_ready?: boolean;
  failover?: {
    state?: 'normal' | 'promoting' | 'failed_over' | 'promotion_failed' | 'failing_back';
    generation?: number;
    active_worker_key?: string | null;
    target_worker_key?: string | null;
    cause?: string | null;
    started_at?: string | null;
    completed_at?: string | null;
  };
  workers: PortalWorkerStatus[];
  queue: {
    queued: number;
    retrying: number;
    running: number;
    done_24h: number;
    failed_24h: number;
    dead_letter: number;
    oldest_queued_seconds: number;
  };
  active_lease: Record<string, unknown>;
  last_job: Record<string, unknown>;
  last_sync: Record<string, unknown>;
  last_booking_acceptance: Record<string, unknown>;
  last_quote: PortalQuoteHealth;
  last_quote_canary: PortalQuoteHealth;
  reconciliation: {
    active_record_count?: number;
    latest_source_total?: number | null;
    last_applied_source_total?: number | null;
    difference_from_latest_source?: number | null;
    pending_preview?: boolean;
  };
  history_30d: {
    samples?: number;
    healthy?: number;
    degraded?: number;
    stale?: number;
    unknown?: number;
    healthy_percent?: number | null;
    oldest_sample_at?: string | null;
    newest_sample_at?: string | null;
  };
}

export const portalAutomationStatusService = {
  async getStatus(signal?: AbortSignal): Promise<PortalAutomationStatus> {
    const request = supabase.rpc('ops_portal_system_status_get');
    const { data, error } = await (signal ? request.abortSignal(signal) : request);
    if (error) throw error;
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      throw new Error('Fora automation status returned an invalid response.');
    }
    return data as unknown as PortalAutomationStatus;
  },

  async createViewerLink(): Promise<{ viewer_url: string; expires_in_seconds: number }> {
    const { data, error } = await supabase.functions.invoke('portal-queue-runner', {
      body: { action: 'fora_viewer_link' },
    });
    if (error) throw error;
    if (!data?.viewer_url) throw new Error(data?.error || 'The secure Fora viewer link could not be created.');
    return {
      viewer_url: String(data.viewer_url),
      expires_in_seconds: Number(data.expires_in_seconds || 300),
    };
  },

  async failbackToPrimary(): Promise<{ state: 'normal' | 'failing_back'; generation: number }> {
    const { data, error } = await supabase.functions.invoke('portal-queue-runner', {
      body: { action: 'fora_failback_primary' },
    });
    if (error) throw error;
    if (!data?.ok || !['normal', 'failing_back'].includes(String(data.state))) {
      throw new Error(data?.error || 'The persistent browser failback could not be started.');
    }
    return {
      state: data.state as 'normal' | 'failing_back',
      generation: Number(data.generation || 0),
    };
  },
};
